import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Div, Headline, Flex, Gutter, SearchInput, Button, Loader, Title, BodyText, ModalDialog } from '@gaiads/telia-react-component-library';
import { useHistory } from 'react-router-dom';
import { PageMetadata, SalesChannel } from '@cdaas/api-clients/dist/product-service-openapi';

import { ProductTableHeaders } from './SalesChannelList/SalesChannelList';
import { selectAllLocales } from '../../store/locales';
import { fetchSalesChannels, removeOneSalesChannel, selectAllSalesChannels, selectSalesChannelsLoading } from '../../store/salesChannels';
import BottomDrawer from './BottomDrawer';
import { Loading } from '../../utils/store';
import { setNotification } from '../../store/ui';
import SalesChannelList from './SalesChannelList/SalesChannelList';
import { ListPagination } from '../../shared/ListUtil/Pagination';
import { productServiceAdminSalesChannelsClient } from '../../api-client/product-service-client';

import './SalesChannels.scss';

export default function SalesChannelsView() {
  const { t } = useTranslation('saleschannelsscreen');

  const dispatch = useDispatch();

  const history = useHistory();

  const salesChannels = useSelector(selectAllSalesChannels);
  const loading = useSelector(selectSalesChannelsLoading);
  const locales = useSelector(selectAllLocales);

  // TODO: implement pagination after getting backend support
  const pageMetadata: PageMetadata = {
    current_page: 0,
    size: 20,
    total_items: 20,
    total_pages: 1,
  };

  const numberOfItemsPerPageOptions = [20, 50, 100];

  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedSalesChannels, setSelectedSalesChannels] = useState<SalesChannel[]>([]);
  const [filteredSalesChannels, setFilteredSalesChannels] = useState<SalesChannel[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [showDeleteSalesChannelConfirmation, setShowDeleteSalesChannelConfirmation] = useState<boolean>(false);

  const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
  const [numberOfItems, setNumberOfItems] = useState<number>(numberOfItemsPerPageOptions[0]);

  const tableHeaders: ProductTableHeaders = [
    undefined,
    t('salesChannelTableHeader.salesChannelName'),
    t('salesChannelTableHeader.salesChannelId'),
    t('salesChannelTableHeader.languages'),
  ];

  const isSelected = (salesChannel: SalesChannel): boolean =>
    selectedSalesChannels.some(existingSalesChannel => existingSalesChannel.id === salesChannel.id);

  const filterSalesChannels = (value: string) => {
    const updatedSalesChannels = salesChannels.filter(salesChannel => salesChannel.name.toLowerCase().includes(value.toLowerCase()));
    setFilteredSalesChannels(updatedSalesChannels);
  };

  useEffect(() => {
    dispatch(fetchSalesChannels());
  }, [dispatch]);

  useEffect(() => {
    if (loading === Loading.FAILED) {
      dispatch(
        setNotification({
          text: t('notification.errorFetch'),
          show: true,
          type: 'warning',
        }),
      );
    }
  }, [loading, dispatch, t]);

  useEffect(() => {
    if (selectedSalesChannels.length > 0) {
      // Update selected variants
      const updatedSelected = salesChannels.filter(salesChannel => isSelected(salesChannel));
      setSelectedSalesChannels(updatedSelected);
    }
    filterSalesChannels(searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesChannels]);

  /* Filters event handlers */

  const onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void = event => {
    setSearchValue(event.target.value);
  };
  const onSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      filterSalesChannels(searchValue);
    }
  };
  const onSearchClick = () => {
    filterSalesChannels(searchValue);
  };

  const resetSelections = () => {
    setSelectedSalesChannels([]);
    setSelectAll(false);
  };

  /* Checkbox event handlers */

  const handleAllChecked = () => {
    const updatedSelectAll = !selectAll;
    let updatedSelection: SalesChannel[] = [];

    if (updatedSelectAll) {
      updatedSelection = [...salesChannels];
    }

    setSelectAll(updatedSelectAll);
    setSelectedSalesChannels(updatedSelection);
  };

  const handleSalesChannelChecked = (salesChannel: SalesChannel) => {
    let updateSelected = [...selectedSalesChannels];

    if (isSelected(salesChannel)) {
      // remove main product
      updateSelected = updateSelected.filter(selectedSalesChannel => selectedSalesChannel.id !== salesChannel.id);
    } else {
      updateSelected.push(salesChannel);
    }

    setSelectedSalesChannels(updateSelected);
  };

  const handleEditSalesChannel = () => {
    history.push(`/saleschannel/${selectedSalesChannels[0].uuid}`);
  };

  const handleDeleteSalesChannels = async () => {
    try {
      for (const salesChannel of selectedSalesChannels) {
        // Sales channels fetched through admin end point and thus they have ids
        await productServiceAdminSalesChannelsClient.deleteSalesChannel(salesChannel.id);
        dispatch(removeOneSalesChannel(salesChannel.id));
      }
    } catch {
      dispatch(
        setNotification({
          text: t('errors.deleteSalesChannel'),
          show: true,
          type: 'warning',
        }),
      );
    }
    setShowDeleteSalesChannelConfirmation(false);
  };

  const TotalCount = ({ count }: { count: number }) => {
    return (
      <Div margin={{ bottom: 'sm' }}>
        <Title tagName="h3" size={'xxs'}>
          {t('count', { count: count })}
        </Title>
      </Div>
    );
  };

  return (
    <Div margin={{ horizontal: 'xxlg', vertical: 'xlg' }}>
      <Flex spaceBetween>
        <Headline size={'md'} padding={{ horizontal: 'xxxs' }}>
          {t('title')}
        </Headline>
        <Button iconName={'plus'} variant="primary" size="md" onClick={() => history.push('/saleschannel')}>
          {t('createSalesChannelButton')}
        </Button>
      </Flex>
      <Gutter size={'xlg'} />
      <Div>
        <SearchInput
          className="daas-sales-channel-list__search-input"
          placeholder={t('filter.textsearchplaceholder')}
          value={searchValue}
          onChange={onSearchChange}
          onKeyPress={onSearchKeyPress}
          onIconClick={onSearchClick}
        />
        <Gutter size="xlg" />
        <Div>{loading === Loading.SUCCEEDED && <TotalCount count={filteredSalesChannels.length} />}</Div>
        <Div padding={{ horizontal: 'xxxs' }}>
          {loading === Loading.PENDING ? (
            <Div alignTextToCenter padding={{ top: 'xlg' }}>
              <Loader size="lg" />
            </Div>
          ) : (
            <SalesChannelList
              salesChannels={filteredSalesChannels}
              locales={locales}
              tableHeaders={tableHeaders}
              onAllChecked={handleAllChecked}
              onSalesChannelChecked={handleSalesChannelChecked}
              isSelected={isSelected}
              selectAll={selectAll}
            />
          )}
        </Div>
        <Gutter size="lg" />
        <ListPagination
          pageMetadata={pageMetadata}
          showText={t('pagination.show')}
          numberOfItemsPerPageOptions={numberOfItemsPerPageOptions}
          numberOfItems={numberOfItems}
          onNumberOfItemsChange={setNumberOfItems}
          currentPageNumber={currentPageNumber}
          onPageNumberChange={setCurrentPageNumber}
        />
      </Div>
      {selectedSalesChannels.length > 0 && (
        <BottomDrawer
          handleEditSalesChannel={handleEditSalesChannel}
          handleRemoveSalesChannels={() => setShowDeleteSalesChannelConfirmation(true)}
          selectedSalesChannels={selectedSalesChannels}
          resetSelections={resetSelections}
        />
      )}
      <ModalDialog
        title={t('deleteSalesChannelConfirmationDialog.title', { count: selectedSalesChannels.length })}
        closeButtonText={t('deleteSalesChannelConfirmationDialog.buttons.cancel')}
        acceptButtonText={t('deleteSalesChannelConfirmationDialog.buttons.delete')}
        isOpen={showDeleteSalesChannelConfirmation}
        onCloseClick={() => setShowDeleteSalesChannelConfirmation(false)}
        onAcceptClick={() => handleDeleteSalesChannels()}
      >
        <BodyText>{t('deleteSalesChannelConfirmationDialog.content', { count: selectedSalesChannels.length })}</BodyText>
      </ModalDialog>
    </Div>
  );
}
